import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { mainTheme } from '../../Theme';
import { TaskDetails } from '../../leadsDetail/LeadsDetailRepository';
import { EnquiryDetail } from '../models/Enquiry';
import {
    buildJourneyData, JourneyTone, JourneyStageIcon, JOURNEY_COLLAPSED_DOT_LIMIT,
} from './journeyData';

// Second of the enquiry header's two Journey widgets - the stage timeline (Received -> each
// follow-up -> Due/Overdue -> Outcome). Split from the stats widget (EnquiryJourneyStatsWidget)
// so it gets its own card and its own height budget, letting the labels/dates use a normal
// font size instead of competing for vertical space inside one shared box. See journeyData.ts
// for the shared derivation both widgets build from.
const useTimelineStyles = makeStyles(() =>
    createStyles({
        // Explicit request: both Journey cards must be the same height. journeyWidgetsRow (and
        // its align-items:'stretch') no longer exists - this is now a direct infoPanelMain flex
        // child (align-items:'flex-start'), so alignSelf:'stretch' below sizes it to the row,
        // the same way EnquiryFormDrawer.tsx's journeyNextFollowUpSlot does for the Next
        // Follow-up card.
        //
        // Explicit bug report: "Received"/"Outcome pending" sat at the top of the card once it
        // stretched taller than its own content, instead of being centered. alignItems:'center'
        // here centers the whole stage row as one unit within that extra height; stageRow
        // (below) keeps its own internal alignItems:'flex-start' so stageNode's dot/label/date
        // column still top-aligns against the connector lines exactly as before - only the row
        // as a whole moves, nothing inside it shifts relative to its neighbours.
        // Explicit bug report: this widget used to be capped inside a fixed flex:'0 0 900px'
        // wrapper shared with EnquiryNextFollowUpWidget (journeyWidgetsRow,
        // EnquiryFormDrawer.tsx), and defaulted to overflow-x:auto - on a wide screen that both
        // hid Due/Outcome pending behind a scrollbar AND left a large empty gap elsewhere in the
        // header, since infoPanelMain's own justify-content:'space-between' never gave this
        // widget the room it actually had. It's now a direct infoPanelMain flex child
        // (EnquiryFormDrawer.tsx), free to grow into the row's real leftover space itself -
        // flex:'1 1 660px' with a 660-880px range, growing with the steps/connectors inside it
        // (both flex:1 1 0 below) rather than needing extra width to just sit empty.
        // overflow-x:auto now only applies as a last-resort fallback on viewports narrow enough
        // (<=640px) that even the reduced 72px step floor can't fit everything - at that point
        // infoPanelMain's own flexWrap has already dropped this widget to its own full-width row
        // beneath identity/next-follow-up, so a true horizontal scroll only matters on genuinely
        // small viewports.
        root: {
            display: 'flex',
            alignItems: 'center',
            flex: '1 1 660px',
            alignSelf: 'stretch',
            minWidth: 660,
            maxWidth: 880,
            padding: '8px 12px',
            borderRadius: 10,
            border: '1px solid rgba(0,0,0,0.08)',
            backgroundColor: 'rgba(0,0,0,0.015)',
            overflowX: 'visible',
            '@media (max-width: 640px)': {
                overflowX: 'auto',
            },
        },
        stageRow: {
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
        },
        // Explicit request: the connector is the flexible part of the strip, sharing whatever
        // space is left alongside the equally-flexible steps (stageNode below) - both flex:1 1 0
        // with their own floor (24px here) so the row grows/shrinks together instead of one side
        // soaking up all the space.
        connector: {
            flex: '1 1 0',
            minWidth: 24,
            height: 32,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
        },
        // The line sits on the connector's own vertical centre (alignItems:'center' above,
        // height:32 matching the circles' own height lands that centre on the circles' shared
        // centre line) - flex:1 fills whatever width the connector flex-resolves to, margin:'0
        // 6px' is the breathing space at each end before it would otherwise touch a circle.
        connectorLine: {
            flex: 1,
            margin: '0 6px',
            height: 0,
            borderTop: '2px solid var(--rv-accent-blue)',
        },
        connectorDashed: {
            borderTopStyle: 'dashed',
            borderTopColor: mainTheme.CommonColor.LightGrey2,
        },
        // Explicit request: the connector leading into an overdue Due step is red, not grey.
        connectorDashedRed: {
            borderTopStyle: 'dashed',
            borderTopColor: 'var(--rv-accent-red)',
        },
        // Explicit correction: steps share whatever width the strip (now flex:'1 1 660px',
        // EnquiryJourneyTimelineWidget's own root above) actually has, flex:1 1 0 same as the
        // connectors between them - every step gets an equal share, so Outcome pending is never
        // narrower than the others by construction. 88px floor normally; 72px on viewports
        // narrow enough that root's own last-resort overflow-x:auto fallback applies too.
        stageNode: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            flex: '1 1 0',
            minWidth: 88,
            '@media (max-width: 640px)': {
                minWidth: 72,
            },
        },
        // Explicit bug report: fixed 32x32 for every step type (check/numbered/due/outcome,
        // including the dashed outline variants below, which only override colour/border) - a
        // circle that changed size per step would throw off the shared horizontal line stageRow
        // (above) now aligns every step to. grid+placeItems centres the number/icon exactly,
        // regardless of whether its content is a single digit or a wider icon glyph.
        stageDot: {
            width: 32,
            height: 32,
            flex: 'none',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            fontSize: '12px',
            fontWeight: 700,
            color: '#fff',
            '& svg': {
                fontSize: '15px',
            },
        },
        // Explicit correction: completed follow-ups are a plain count, not an urgency signal -
        // blue instead of the old orange (JOURNEY_FOLLOW_UP_COLOR), which is reserved for "due
        // today" on the new Next Follow-up header widget instead.
        dotBlue: { backgroundColor: 'var(--rv-accent-blue)' },
        dotRed: { backgroundColor: 'var(--rv-accent-red)' },
        dotGreen: { backgroundColor: 'var(--rv-accent-green)' },
        // Explicit correction: the due/overdue stage node now follows the same red-for-overdue
        // convention as the Next Follow-up header widget - supersedes this file's earlier
        // "no red, stays orange family" rule for that same node.
        dotBlueOutline: {
            backgroundColor: '#fff',
            color: 'var(--rv-accent-blue) !important',
            border: '2px dashed var(--rv-accent-blue)',
        },
        dotRedOutline: {
            backgroundColor: '#fff',
            color: 'var(--rv-accent-red) !important',
            border: '2px dashed var(--rv-accent-red)',
        },
        // Explicit bug report: the Due step had no amber state at all - every non-overdue date
        // fell through to blue, whether upcoming or due today. Matches
        // EnquiryNextFollowUpWidget's own amber "due today" treatment for the same date.
        dotOrangeOutline: {
            backgroundColor: '#fff',
            color: 'var(--rv-accent-orange) !important',
            border: '2px dashed var(--rv-accent-orange)',
        },
        dotGrey: {
            backgroundColor: '#fff',
            color: `${mainTheme.TextColor.Secondary} !important`,
            border: `2px dashed ${mainTheme.CommonColor.LightGrey2}`,
        },
        // Explicit bug report: labels that happened to wrap to one line left less vertical
        // space above the date than labels wrapping to two ("Meeting Booked"), so the date row
        // didn't land on a shared line either, even once the circles (above) did. minHeight
        // reserves space for 2 lines unconditionally - a 1-line label still only shows 1 line,
        // but every stageLabel occupies the same height regardless, so stageDate always starts
        // at the same offset. line-clamp (plus the title attribute in the JSX, for the full
        // text) handles a label that would've run past 2 lines; wordBreak still stops a single
        // long unbroken token from overrunning the column's own width.
        stageLabel: {
            marginTop: 4,
            fontSize: '12.5px',
            fontWeight: 600,
            color: mainTheme.TextColor.Primary,
            textAlign: 'center',
            lineHeight: 1.25,
            wordBreak: 'break-word',
            display: '-webkit-box',
            // Explicit request: the header was too tall - the label (and date below) now take one line each
            // instead of reserving two. Longer text clips with an ellipsis; both carry a title with the
            // full text. Every step still shares one height, so the date row stays on one line across steps.
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '15.63px', // 1 line at 1.25 line-height / 12.5px font-size
            // Explicit request: a 2-word label wrapping at the browser's default break point
            // could leave a lone short word on its own second line ("Internal Follow Up" ->
            // "Internal Follow" / "Up") - balance re-distributes the break more evenly instead.
            // tslint:disable-next-line: no-any
            textWrap: 'balance' as any,
        },
        stageDate: {
            fontSize: '12px',
            color: mainTheme.TextColor.Secondary,
            textAlign: 'center',
            wordBreak: 'break-word',
            // A matter title can run long - 1 line then clip (full text in the title attribute)
            // rather than pushing the card taller or overflowing.
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
        },
        // Explicit request: the Due step's date is always a single short date ("12 Oct") and
        // must never wrap - a fully self-contained variant (not combined with stageDate above,
        // so there's no risk of the two classes' overflow/display rules fighting over
        // declaration order) used only for that one step.
        stageDateNoWrap: {
            fontSize: '12px',
            color: mainTheme.TextColor.Secondary,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        // Explicit bug report: the outcome stage's text ("Outcome pending"/"Matter created" +
        // a matter title) is the longest of any stage - the other stageNodes' 68px width is
        // fine for "Received"/"Follow-up 3"/"Due", but was too narrow here.
        stageNodeWide: {
            width: 96,
        },
        // Explicit bug report: this used to be a bordered card wrapping all three of the
        // collapsed step's rows (count, dots, last-done date) at once - the fix is for the
        // collapsed step to follow the exact same 3-row structure as every other step instead
        // (collapsedStageNode below), with this pill now ONLY row 1 - no border-around-
        // everything, no fixed width/height beyond the 32px that matches the circles' own
        // height. width:max-content + whiteSpace:nowrap (not a fixed width or overflow:hidden)
        // is what keeps "N follow-ups" fully visible at any panel width.
        collapsedPill: {
            height: 32,
            boxSizing: 'border-box',
            width: 'max-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '0 12px',
            borderRadius: 999,
            border: '1px solid var(--rv-accent-blue)',
            backgroundColor: 'var(--rv-accent-blue-bg)',
            color: 'var(--rv-accent-blue)',
            fontSize: '13px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            '& svg': {
                fontSize: '16px',
            },
        },
        // Explicit request: the collapsed step's own column - width:max-content (not a fixed
        // width) with a 150px floor so "N follow-ups" is never clipped regardless of how wide
        // the count or dot row gets; flexShrink:0 keeps it from being squeezed by the row's own
        // flex layout the way the fixed-width plain steps (stageNode above) never need to worry
        // about since they're flex:'0 0 92px' already.
        collapsedStageNode: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            width: 'max-content',
            minWidth: 150,
            flexShrink: 0,
        },
        // Explicit request: row 2 (the dot row) occupies the same reserved height as every other
        // step's own label row (stageLabel's own 31.25px minHeight) so row 3 (the date, reusing
        // stageDate directly below) lines up on one shared baseline across every step, collapsed
        // or not.
        collapsedDotsRow: {
            marginTop: 4,
            minHeight: '15.63px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        // Explicit request: one dot per completed follow-up, every dot filled - no hollow dot
        // (the Due step, always its own stage now, represents "a follow-up is pending" instead).
        // flexWrap:'nowrap' keeps the capped dot count (JOURNEY_COLLAPSED_DOT_LIMIT) on one row;
        // no overflow:hidden needed any more since the column itself sizes to fit (width:
        // max-content on collapsedStageNode above), nothing here needs clipping.
        collapsedDots: {
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flexWrap: 'nowrap',
        },
        collapsedDot: {
            width: 7,
            height: 7,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: 'var(--rv-accent-blue)',
        },
    })
);

interface EnquiryJourneyTimelineWidgetProps {
    enquiryDetail: EnquiryDetail;
    tasks: TaskDetails[];
}

const stageIcon = (iconKind: JourneyStageIcon, numberValue?: number): React.ReactNode => {
    switch (iconKind) {
        case 'received': return <CheckOutlinedIcon />;
        case 'number': return <>{numberValue}</>;
        case 'due': return <AccessTimeIcon />;
        case 'overdue': return <ReportProblemOutlinedIcon />;
        case 'matter': return <WorkOutlineIcon />;
        case 'flag': return <FlagOutlinedIcon />;
        default: return null;
    }
};

const EnquiryJourneyTimelineWidgetInner: React.FC<EnquiryJourneyTimelineWidgetProps> = ({ enquiryDetail, tasks }) => {
    const classes = useTimelineStyles();
    const { stages } = buildJourneyData(enquiryDetail, tasks);

    const dotClass = (tone: JourneyTone, outline?: boolean) => {
        if (outline) {
            if (tone === 'grey') {
                return classes.dotGrey;
            }
            if (tone === 'red') {
                return classes.dotRedOutline;
            }
            // Explicit bug report: 'orange' (due today) used to fall through to the same blue as
            // upcoming - now has its own dashOutline variant, matching EnquiryNextFollowUpWidget's
            // 3-tone (blue/orange/red) scheme for the same date instead of a 2-tone one.
            return tone === 'orange' ? classes.dotOrangeOutline : classes.dotBlueOutline;
        }
        return classes[`dot${tone.charAt(0).toUpperCase()}${tone.slice(1)}` as 'dotBlue'];
    };

    // Explicit request: every connector (now the flexible part of the strip - see connector's
    // own comment) is solid blue between completed steps, dashed grey from the last completed
    // step onward, dashed red specifically leading into an overdue Due step - undefined (plain
    // solid connectorLine) when the step ahead isn't dashed at all. journeyData.ts always pairs
    // tone:'red' with dashed:true for the Due stage specifically, never for any other dashed
    // stage, so a due-today 'orange' Due step is still just dashed grey here - only the circle
    // and label themselves turn amber for that state.
    const connectorLineClass = (stage: typeof stages[number]): string | undefined => {
        if (stage.type !== 'node' || !stage.dashed) {
            return undefined;
        }
        return stage.tone === 'red' ? classes.connectorDashedRed : classes.connectorDashed;
    };

    // Explicit request: the Due step's label itself (not just its circle) reads amber/red for
    // due-today/overdue - 'Due' (upcoming) stays the same neutral ink every other step's label
    // uses, matching the rest of the strip's "colour means urgency" convention rather than
    // tinting a label that isn't urgent.
    const dueLabelColor = (tone: JourneyTone): string | undefined => {
        if (tone === 'red') {
            return 'var(--rv-accent-red)';
        }
        return tone === 'orange' ? 'var(--rv-accent-orange)' : undefined;
    };

    return (
        <div className={classes.root}>
            <div className={classes.stageRow}>
            {stages.map((stage, index) => (
                <React.Fragment key={stage.key}>
                    {index > 0 && (
                        <span className={classes.connector}>
                            <span className={clsx(classes.connectorLine, connectorLineClass(stage))} />
                        </span>
                    )}
                    {stage.type === 'collapsed' ? (
                        // Explicit bug report: this used to be one bordered box wrapping all
                        // three rows (count, dots, last-done date), which also used to carry the
                        // Due step's own "next"/overdue date - duplicating both the follow-up
                        // count (already on EnquiryNextFollowUpWidget's own badge) and the
                        // overdue date. Now follows the exact same 3-row structure as every other
                        // step: a pill (row 1, same height/position as a circle), the dot row
                        // (row 2, same reserved height as a label), the last-done date (row 3,
                        // literally the same stageDate class every other step's date uses) - Due
                        // itself is always its own separate step, built by journeyData.ts right
                        // after this one.
                        <div className={classes.collapsedStageNode}>
                            <span className={classes.collapsedPill}>
                                <AutorenewIcon />
                                {stage.count} follow-ups
                            </span>
                            <span className={classes.collapsedDotsRow}>
                                <span className={classes.collapsedDots}>
                                    {/* Explicit request: every dot here is a completed follow-up,
                                        so every dot is filled - no hollow dot (the Due step,
                                        always its own stage now, represents "pending" instead). */}
                                    {Array.from({ length: Math.min(stage.count, JOURNEY_COLLAPSED_DOT_LIMIT) }).map((_unused, dotIndex) => (
                                        // tslint:disable-next-line: jsx-no-lambda
                                        <span key={dotIndex} className={classes.collapsedDot} />
                                    ))}
                                </span>
                            </span>
                            <span className={classes.stageDate}>Last {stage.lastDoneLabel}</span>
                        </div>
                    ) : (
                        <div className={classes.stageNode}>
                            <span className={clsx(classes.stageDot, dotClass(stage.tone, !!stage.dashed))}>
                                {stageIcon(stage.iconKind, stage.numberValue)}
                            </span>
                            <span
                                className={classes.stageLabel}
                                title={stage.label}
                                style={stage.key === 'due' ? { color: dueLabelColor(stage.tone) } : undefined}
                            >
                                {stage.label}
                            </span>
                            {!!stage.date && (
                                <span className={clsx(stage.key === 'due' ? classes.stageDateNoWrap : classes.stageDate)} title={stage.date}>
                                    {stage.date}
                                </span>
                            )}
                        </div>
                    )}
                </React.Fragment>
            ))}
            </div>
        </div>
    );
};

// Only the fields this widget actually reads - see EnquiryJourneyStatsWidget's own identical
// comparator for why (same rationale, same source data, just the sibling card).
const areTimelinePropsEqual = (prevProps: EnquiryJourneyTimelineWidgetProps, nextProps: EnquiryJourneyTimelineWidgetProps): boolean => {
    return prevProps.tasks === nextProps.tasks
        && prevProps.enquiryDetail.createdDate === nextProps.enquiryDetail.createdDate
        && prevProps.enquiryDetail.matterGuid === nextProps.enquiryDetail.matterGuid
        && prevProps.enquiryDetail.matterTitle === nextProps.enquiryDetail.matterTitle
        && prevProps.enquiryDetail.outcome === nextProps.enquiryDetail.outcome
        && prevProps.enquiryDetail.followUpDueDate === nextProps.enquiryDetail.followUpDueDate;
};

export const EnquiryJourneyTimelineWidget = React.memo(EnquiryJourneyTimelineWidgetInner, areTimelinePropsEqual);
